import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/shared/empty-state';
import { Plus, Clock, Shield, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { SCAN_TYPE_LABELS, type ScanStatus, type ScanType } from '@nirvik/shared';

interface ScanItem {
  scanId: string;
  scanType: ScanType;
  status: ScanStatus;
  createdAt: string;
  duration: number | null;
  findingCount: number;
}

const statusStyles: Record<ScanStatus, string> = {
  completed: 'bg-green-100 text-green-700 border-green-200',
  running: 'bg-blue-100 text-blue-700 border-blue-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  queued: 'bg-gray-100 text-muted-foreground border-gray-200',
  cancelled: 'bg-gray-100 text-muted-foreground border-gray-200',
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function ScansPage() {
  const { orgId, portfolioId, projectId } = useParams<{ orgId: string; portfolioId: string; projectId: string }>();
  const navigate = useNavigate();
  const enabled = !!orgId && !!portfolioId && !!projectId;

  const { data, isLoading } = useQuery({
    queryKey: ['scans', orgId, portfolioId, projectId],
    queryFn: () => apiClient.listScans(orgId!, portfolioId!, projectId!),
    enabled,
  });

  const scans = (data?.data as ScanItem[] | undefined) || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-4 bg-gray-100 dark:bg-muted rounded w-32" />
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-gray-100 dark:bg-muted rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-muted rounded w-40 mb-2" />
                  <div className="h-3 bg-gray-100 dark:bg-muted rounded w-56" />
                </div>
                <div className="h-6 bg-gray-100 dark:bg-muted rounded w-20" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Scan History</h1>
          <p className="text-muted-foreground dark:text-muted-foreground mt-1">All security scans for this project</p>
        </div>
        <Link to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans/new`}>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Scan
          </Button>
        </Link>
      </div>

      {scans.length === 0 ? (
        <EmptyState
          icon={Shield}
          title="No scans yet"
          description="Run your first scan to check this project for security issues."
          actionLabel="Start a Scan"
          onAction={() => {
            navigate(`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans/new`);
          }}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {scans.map((scan) => (
            <Link key={scan.scanId} to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans/${scan.scanId}`} className="block">
              <Card className="p-4 hover:bg-gray-50 dark:hover:bg-muted transition-colors cursor-pointer">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'h-10 w-10 rounded-lg flex items-center justify-center',
                        scan.status === 'completed' && 'bg-green-50',
                        scan.status === 'running' && 'bg-blue-50',
                        scan.status === 'failed' && 'bg-red-50',
                        scan.status === 'queued' && 'bg-gray-50',
                        scan.status === 'cancelled' && 'bg-gray-50'
                      )}
                    >
                      {scan.status === 'running' ? (
                        <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                      ) : (
                        <Shield
                          className={cn(
                            'h-5 w-5',
                            scan.status === 'completed' && 'text-green-600',
                            scan.status === 'failed' && 'text-red-600',
                            (scan.status === 'queued' || scan.status === 'cancelled') && 'text-muted-foreground'
                          )}
                        />
                      )}
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-foreground">
                        {SCAN_TYPE_LABELS[scan.scanType] ?? scan.scanType}
                      </p>
                      <p className="text-xs text-muted-foreground dark:text-muted-foreground flex items-center gap-2 mt-0.5">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatRelativeTime(scan.createdAt)}
                        </span>
                        {scan.duration !== null && (
                          <>
                            <span className="text-foreground dark:text-muted-foreground">|</span>
                            <span>{formatDuration(scan.duration)}</span>
                          </>
                        )}
                        {scan.findingCount > 0 && (
                          <>
                            <span className="text-foreground dark:text-muted-foreground">|</span>
                            <span>{scan.findingCount} findings</span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  <Badge
                    variant="outline"
                    className={cn('font-medium capitalize', statusStyles[scan.status])}
                  >
                    {scan.status === 'running' && (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    )}
                    {scan.status}
                  </Badge>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
